import { create } from 'zustand';
import type { ChatMessageEvent, AiActionStatus, ReactionAgg } from '../types/chat';
// chat-read-receipts — lastReadId 는 BigInt 문자열이라 Number 비교가 2^53 에서 깨진다.
import { isIdGreater as isReadIdNewer } from '../lib/chat-read';

/**
 * Chat state (P2-15) — 시안 50 useState 중 채팅 영역만 분리.
 *
 * 시안 매핑 (WorkChat.jsx line 336~352):
 *   - activeChatId        → 현재 활성 room id (string, BigInt → string SSOT)
 *   - messages            → messagesByRoom (per-room 분리, room 전환 시에도 보존)
 *   - inputValue          → inputValueByRoom (room 별 미입력 보존, 시안 1개 globalState 와 차이)
 *   - chatSearch          → chatSearch (활성 room 의 in-room 검색)
 *   - chatFilter          → chatFilter ('all' | 'direct' | 'team' | 'group')
 *   - hoveredMsg          → hoveredMsgId (메시지 hover action bar 표시용)
 *   - expandedDept        → expandedDept (조직도 left panel — Phase 1 P1-13 에서 사용)
 *
 * 메시지 저장:
 *   - messagesByRoom: Record<roomId, ChatMessageEvent[]> — room 별 array.
 *   - dedup by commitSeq — useSSE 의 catch-up 과 pubsub 동시 push 시 중복 차단.
 *   - sort by commitSeq asc (자연 도착 순서, oldest first; UI 가 reverse render).
 *
 * 본 store 는 SSE 가 단일 source of truth — useSSE.onMessage 가 appendMessage 호출.
 * 사용자가 보낸 메시지는 POST 응답 받고 appendMessage (optimistic UI 미사용 — 시안 단순).
 */

export type ChatFilter = 'all' | 'direct' | 'team' | 'group';

/**
 * AI 응답 streaming 임시 메시지 (adr-20260430-2235-ai-gateway-streaming).
 *
 * delta chunk 가 도착할 때마다 `text` 누적. ai_complete 도달 시 `text` 를 final 로 교체 +
 * `isStreaming=false`. 이후 outbox 의 final sse.message 가 도달하면 messagesByRoom 에 정식 등록되고
 * 본 임시 메시지는 정리 (`removeStream`).
 *
 * commitSeq 가 부재 — messagesByRoom 의 정식 메시지와 별도 영역에 저장. UI 는 두 영역을 함께 렌더.
 */
export interface StreamingMessage {
  /** UUID v4 — chat-server 가 발급. delta·complete 매칭 키. */
  streamMessageId: string;
  /** 누적 또는 최종 텍스트. complete 이후엔 final unmasked. */
  text: string;
  /** delta 진행 중 (cursor blink) vs complete 도달 (cursor 종료) 구분. */
  isStreaming: boolean;
  /** UI 정렬용 — 첫 chunk 도착 시각. */
  createdAt: string;
  /**
   * R4 fix (followup-cycle-streaming-C-leak): 마지막 chunk 도착 시각.
   * `evictStaleStreams` 가 isStreaming=true + lastChunkAt 5분 경과 entry 를 강제 제거 (leak 안전망).
   * appendChunk 마다 갱신, finalizeStream 시점에도 갱신.
   */
  lastChunkAt: string;
}

interface ChatState {
  activeChatId: string | null;
  messagesByRoom: Record<string, ChatMessageEvent[]>;
  inputValueByRoom: Record<string, string>;
  /** AI 비서 모드 토글 — room 별. send 시 content 에 '/ai ' prefix 자동 부착. 페이지 reload 시 reset. */
  aiModeByRoom: Record<string, boolean>;
  /** active room 의 멤버 수 — ChatArea 가 getRoom fetch 후 set. ChatList active 카드가 표시. */
  roomMemberCountByRoom: Record<string, number>;
  /** AI 응답 대기 중 표시 — /ai 메시지 send 직후 true, AI 응답(isAi=true) 도착 시 자동 false. */
  aiPendingByRoom: Record<string, boolean>;
  /** AI 도구 사용 중 — orchestrator 의 sse.ai_tool_use 'start' 시 set, 'done' 또는 AI 응답 도착 시 clear. null=도구 사용 아님(생각 중). */
  aiToolByRoom: Record<string, string | null>;
  chatSearch: string;
  chatFilter: ChatFilter;
  hoveredMsgId: string | null;
  // 미읽음 배지 SSOT — roomId→미읽음수 맵 + 합계. NavBar(합계)·ChatList(방별)·실시간 SSE(bump) 공유.
  roomUnread: Record<string, number>;
  chatUnreadTotal: number;
  // 답장 대상 메시지 — room별. 입력창 인용 배너 + 전송 시 replyToId 결합.
  replyingToByRoom: Record<string, ChatMessageEvent>;
  // 메시지별 이모지 반응 — 본인 토글 시 override. 없으면 메시지 임베드 reactions fallback.
  reactionsByMessageId: Record<string, ReactionAgg[]>;
  expandedDept: Record<string, boolean>;
  /**
   * AI action 의 latest status overlay (P2-16f Phase 5 SSE 결선).
   * approve/reject API 응답 + sse.ai_action_status push 가 본 map 을 갱신.
   * LeaveRequestCard 등 sub-card 가 본 overlay 우선, 부재 시 message.aiActionData.status fallback.
   * 본 SSOT 가 다중 탭/디바이스 자동 sync 의 단일 권위.
   */
  actionsByActionId: Record<string, AiActionStatus>;
  /**
   * AI streaming 임시 메시지 영역 (adr-20260430-2235).
   * 같은 roomId 안의 multiple in-flight stream 도 streamMessageId 별로 분리 저장.
   * final sse.message 도착 시 removeStream 으로 cleanup.
   */
  streamingByRoom: Record<string, Record<string, StreamingMessage>>;
  /**
   * 이전(과거) 페이지 cursor — room 별. 초기 로드 + 이전 로드 응답의 nextCursor 를 보관.
   *   - undefined: 아직 초기 로드 안 됨 (버튼 미노출)
   *   - string   : 더 과거 메시지 있음 (이 cursor 로 이전 로드, '이전 대화 불러오기' 버튼 노출)
   *   - null     : 대화 시작 도달 (더 없음, 버튼 미노출)
   */
  historyCursorByRoom: Record<string, string | null>;
  /** 이전 메시지 로드 in-flight — 중복 클릭/요청 방지 + 버튼 spinner. */
  loadingOlderByRoom: Record<string, boolean>;
  /**
   * 대시보드 퀵액션 등에서 채팅 입력창에 미리 채울 일회성 텍스트.
   * MessageInput 이 마운트/활성화 시 1회 소비 후 null 로 clear (ChatPage 로직 무수정).
   */
  pendingComposeText: string | null;
  /**
   * chat-read-receipts (2026-07-29) — 방별 (userId → lastReadId) 맵.
   *
   * 이 맵 하나로 "메시지 M 을 아직 안 읽은 인원" = `lastReadId < M.id` 인 타 멤버 수를 계산한다.
   * 진입 시 GET /read-states 로 채우고, 이후 SSE `chat_read` 로 한 명씩 증분 갱신.
   * lastReadId 는 문자열 BigInt — 비교는 lib/chat-read.ts 의 자릿수-우선 비교자 사용.
   */
  readStatesByRoom: Record<string, Record<string, string | null>>;
  /**
   * chat-read-receipts — 미읽음 구분선 앵커. 방 **진입 시점** 의 내 lastReadId 스냅샷.
   *
   * 진입 즉시 서버 lastReadId 는 최신으로 전진하지만, 구분선은 화면에 머무는 동안 고정돼야
   * "어디부터 안 읽었는지" 를 잃지 않는다. 그래서 서버 값과 분리해 방 전환 시에만 갱신한다.
   *   - undefined: 아직 조회 전 (구분선 미표시)
   *   - null     : 한 번도 읽은 적 없음 (첫 메시지 위에 구분선)
   *   - string   : 이 id 다음 메시지 위에 구분선
   */
  unreadAnchorByRoom: Record<string, string | null>;

  setActiveChat: (roomId: string | null) => void;
  /**
   * 메시지 추가. commitSeq dedup + asc 정렬 보장.
   * SSE catch-up 과 pubsub 의 중복 push 시 두 번째 호출은 no-op.
   */
  appendMessage: (msg: ChatMessageEvent) => void;
  /** 여러 메시지 일괄 추가 — catch-up 결과 push 등. */
  appendMessages: (msgs: ChatMessageEvent[]) => void;
  /**
   * 메시지 삭제(전체 회수) — 해당 메시지를 톰스톤(isDeleted=true + 본문/첨부 블랭크)으로 갱신.
   * SSE 'message_deleted' 수신 + 본인 삭제 optimistic 양쪽에서 호출. (chat-message-delete)
   */
  markMessageDeleted: (roomId: string, messageId: string) => void;
  /** room 의 모든 메시지 교체 — 초기 fetch 후 사용. */
  setMessages: (roomId: string, msgs: ChatMessageEvent[]) => void;
  /** 이전 페이지 cursor 갱신 — 초기/이전 로드 응답의 nextCursor 반영. */
  setHistoryCursor: (roomId: string, cursor: string | null) => void;
  /** 이전 로드 in-flight 플래그 set. */
  setLoadingOlder: (roomId: string, loading: boolean) => void;
  /** 퀵액션 prefill 텍스트 set/clear (null=소비 완료). */
  setPendingComposeText: (text: string | null) => void;
  /**
   * chat-read-receipts — GET /read-states 응답 반영. anchor 는 방을 처음 열 때만 기록해
   * (이미 있으면 유지) 재조회로 구분선이 사라지지 않게 한다.
   */
  setRoomReadStates: (roomId: string, states: Array<{ userId: string; lastReadId: string | null }>, myLastReadId: string | null) => void;
  /** chat-read-receipts — SSE `chat_read` 증분 반영. 뒤로 가는 갱신은 무시(단조 전진). */
  applyReadReceipt: (roomId: string, userId: string, lastReadId: string) => void;
  /** chat-read-receipts — 방 나갈 때 구분선 앵커만 폐기(다음 진입 시 재계산). */
  clearUnreadAnchor: (roomId: string) => void;
  setInputValue: (roomId: string, value: string) => void;
  clearInput: (roomId: string) => void;
  /** AI 비서 모드 토글 (room 별). 활성 시 send 메시지에 자동 '/ai ' prefix. */
  toggleAiMode: (roomId: string) => void;
  /** active room 의 멤버 수 set (ChatArea getRoom fetch 후). */
  setRoomMemberCount: (roomId: string, count: number) => void;
  /** AI 응답 대기 상태 set (MessageInput 가 /ai 메시지 send 시 true). */
  setAiPending: (roomId: string, pending: boolean) => void;
  /** AI 도구 사용 상태 set — null 이면 "생각 중", 도구명이 있으면 "도구 사용 중: <tool>". */
  setAiTool: (roomId: string, tool: string | null) => void;
  setSearch: (q: string) => void;
  setFilter: (f: ChatFilter) => void;
  setHoveredMsg: (id: string | null) => void;
  /** listMyRooms 결과로 roomId→미읽음 맵 전체 교체 (+ 합계 재계산). */
  setRoomUnreads: (map: Record<string, number>) => void;
  /** 실시간 새 메시지 — 해당 방 미읽음 +1 (보고 있지 않은 방). */
  bumpRoomUnread: (roomId: string) => void;
  /** 방 열람/읽음 — 해당 방 미읽음 0. */
  clearRoomUnread: (roomId: string) => void;
  /** 답장 시작 — 입력창 위 인용 배너 표시. */
  setReplyingTo: (roomId: string, message: ChatMessageEvent) => void;
  /** 답장 취소 / 전송 완료 — 인용 배너 해제. */
  clearReplyingTo: (roomId: string) => void;
  /** 본인 반응 토글 결과를 로컬 반영 — base 는 현재 표시 중 reactions(메시지 임베드 fallback). */
  applyMyReaction: (messageId: string, base: ReactionAgg[], emoji: string, action: 'added' | 'removed') => void;
  toggleDept: (key: string) => void;
  /**
   * AI action status overlay 갱신 — approve/reject 응답 또는 sse.ai_action_status push 가 호출.
   * 같은 actionId 가 이미 같은 status 면 no-op (참조 변경 방지로 LeaveRequestCard 재렌더 회피).
   */
  updateAction: (actionId: string, status: AiActionStatus) => void;
  /**
   * AI streaming delta — chunk 누적 (adr-20260430-2235).
   * 첫 호출 시 임시 메시지 생성, 이후는 text concat. SSE event 'ai_delta' 가 trigger.
   */
  appendChunk: (roomId: string, streamMessageId: string, delta: string) => void;
  /**
   * AI streaming 종료 — final unmasked text 로 교체 + isStreaming=false.
   * SSE event 'ai_complete' 가 trigger. 같은 streamMessageId 가 부재면 (delta 누락 race) 새로 생성.
   */
  finalizeStream: (roomId: string, streamMessageId: string, finalText: string) => void;
  /**
   * AI streaming 임시 메시지 정리 — outbox 의 final sse.message 도착 시 호출.
   * UI 가 정식 ChatMessageEvent 로 교체되면 임시 메시지는 더 이상 필요 없음.
   */
  removeStream: (roomId: string, streamMessageId: string) => void;
  /**
   * R4 fix (followup-cycle-streaming-C-leak) — anthropic timeout 등 ai.complete 미발화 시 partial entry leak 차단.
   * isStreaming=true + lastChunkAt > maxAgeMs 인 entry 강제 제거.
   *
   * F-MED-2 fix (followup-D): roomId 'all' sentinel 의 type narrowing 취약점 → 함수 분리.
   * 단일 room: evictStaleStreamsInRoom(roomId, maxAgeMs)
   * 전 room:   evictStaleStreamsAll(maxAgeMs)        ← ChatPage 의 1분 setInterval
   */
  evictStaleStreamsInRoom: (roomId: string, maxAgeMs: number) => void;
  evictStaleStreamsAll: (maxAgeMs: number) => void;
  /** 로그아웃 / 세션 클리어 — 모든 chat state reset. */
  reset: () => void;
}

/** invalid commitSeq (non-numeric) → silent drop. backend 가 보장하지만 방어적 가드. */
function safeParseSeq(s: string): bigint | null {
  if (typeof s !== 'string' || !/^[0-9]+$/.test(s)) return null;
  try { return BigInt(s); } catch { return null; }
}

/**
 * evictStaleStreamsInRoom / evictStaleStreamsAll 의 공통 로직.
 * mutation 없으면 동일 state 반환 (zustand ref equality 가드 — 불필요한 re-render 차단).
 */
function _evictStaleStreams(
  state: ChatState,
  targetRooms: string[],
  maxAgeMs: number,
): Pick<ChatState, 'streamingByRoom'> | ChatState {
  const cutoff = Date.now() - maxAgeMs;
  let mutated = false;
  const nextStreaming = { ...state.streamingByRoom };
  for (const rid of targetRooms) {
    const room = state.streamingByRoom[rid];
    if (!room) continue;
    const filtered: Record<string, StreamingMessage> = {};
    let roomMutated = false;
    for (const [sid, msg] of Object.entries(room)) {
      // isStreaming=false 는 cleanup effect 가 처리 — evict 대상 아님.
      if (!msg.isStreaming) {
        filtered[sid] = msg;
        continue;
      }
      if (new Date(msg.lastChunkAt).getTime() < cutoff) {
        // stale → drop
        roomMutated = true;
        continue;
      }
      filtered[sid] = msg;
    }
    if (roomMutated) {
      mutated = true;
      nextStreaming[rid] = filtered;
    }
  }
  return mutated ? { streamingByRoom: nextStreaming } : state;
}

function insertSorted(arr: ChatMessageEvent[], msg: ChatMessageEvent): ChatMessageEvent[] {
  // dedup by commitSeq.
  if (arr.some((m) => m.commitSeq === msg.commitSeq)) return arr;
  const insertedSeq = safeParseSeq(msg.commitSeq);
  if (insertedSeq == null) return arr; // invalid seq — silent drop (review batch-16 봉합).
  const lastSeq = arr.length > 0 ? safeParseSeq(arr[arr.length - 1]!.commitSeq) : null;
  // 대부분 새 메시지가 마지막에 도착 — fast path.
  if (arr.length === 0 || (lastSeq != null && lastSeq < insertedSeq)) {
    return [...arr, msg];
  }
  // out-of-order (catch-up race) — 정렬된 위치 삽입.
  const next = [...arr];
  let lo = 0;
  let hi = next.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const midSeq = safeParseSeq(next[mid]!.commitSeq);
    if (midSeq != null && midSeq < insertedSeq) lo = mid + 1;
    else hi = mid;
  }
  next.splice(lo, 0, msg);
  return next;
}

function mergeSorted(existing: ChatMessageEvent[], incoming: ChatMessageEvent[]): ChatMessageEvent[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((m) => m.commitSeq));
  // dedup incoming 내부 + invalid seq drop.
  const filtered: ChatMessageEvent[] = [];
  for (const m of incoming) {
    if (seen.has(m.commitSeq)) continue;
    if (safeParseSeq(m.commitSeq) == null) continue;
    seen.add(m.commitSeq);
    filtered.push(m);
  }
  if (filtered.length === 0) return existing;
  const merged = [...existing, ...filtered];
  merged.sort((a, b) => {
    const ax = safeParseSeq(a.commitSeq); const bx = safeParseSeq(b.commitSeq);
    if (ax == null || bx == null) return 0;
    return ax < bx ? -1 : ax > bx ? 1 : 0;
  });
  return merged;
}

const INITIAL_STATE: Pick<ChatState, 'activeChatId' | 'messagesByRoom' | 'inputValueByRoom' | 'aiModeByRoom' | 'roomMemberCountByRoom' | 'aiPendingByRoom' | 'aiToolByRoom' | 'chatSearch' | 'chatFilter' | 'hoveredMsgId' | 'roomUnread' | 'chatUnreadTotal' | 'replyingToByRoom' | 'reactionsByMessageId' | 'expandedDept' | 'actionsByActionId' | 'streamingByRoom' | 'historyCursorByRoom' | 'loadingOlderByRoom' | 'pendingComposeText' | 'readStatesByRoom' | 'unreadAnchorByRoom'> = {
  activeChatId: null,
  messagesByRoom: {},
  inputValueByRoom: {},
  aiModeByRoom: {},
  roomMemberCountByRoom: {},
  aiPendingByRoom: {},
  aiToolByRoom: {},
  chatSearch: '',
  chatFilter: 'all',
  hoveredMsgId: null,
  roomUnread: {},
  chatUnreadTotal: 0,
  replyingToByRoom: {},
  reactionsByMessageId: {},
  expandedDept: {},
  actionsByActionId: {},
  streamingByRoom: {},
  historyCursorByRoom: {},
  loadingOlderByRoom: {},
  pendingComposeText: null,
  readStatesByRoom: {},
  unreadAnchorByRoom: {},
};

export const useChatStore = create<ChatState>()((set) => ({
  ...INITIAL_STATE,
  setActiveChat: (roomId) => set({ activeChatId: roomId, hoveredMsgId: null, chatSearch: '' }),
  appendMessage: (msg) => set((state) => {
    const cur = state.messagesByRoom[msg.roomId] ?? [];
    const next = insertSorted(cur, msg);
    if (next === cur) return state; // dedup hit — no change.
    const patch: Partial<ChatState> = {
      messagesByRoom: { ...state.messagesByRoom, [msg.roomId]: next },
    };
    // AI 응답(isAi=true) 도착 시 thinking placeholder + tool 상태 자동 hide.
    if (msg.isAi && state.aiPendingByRoom[msg.roomId]) {
      patch.aiPendingByRoom = { ...state.aiPendingByRoom, [msg.roomId]: false };
    }
    if (msg.isAi && state.aiToolByRoom[msg.roomId]) {
      patch.aiToolByRoom = { ...state.aiToolByRoom, [msg.roomId]: null };
    }
    return patch;
  }),
  markMessageDeleted: (roomId, messageId) => set((state) => {
    const cur = state.messagesByRoom[roomId];
    if (!cur) return state;
    let changed = false;
    const next = cur.map((m) => {
      if (m.id === messageId && !m.isDeleted) {
        changed = true;
        return { ...m, isDeleted: true, content: '', attachments: null };
      }
      return m;
    });
    if (!changed) return state;
    return { messagesByRoom: { ...state.messagesByRoom, [roomId]: next } };
  }),
  appendMessages: (msgs) => set((state) => {
    if (msgs.length === 0) return state;
    const byRoom: Record<string, ChatMessageEvent[]> = {};
    for (const m of msgs) {
      if (!byRoom[m.roomId]) byRoom[m.roomId] = [];
      byRoom[m.roomId]!.push(m);
    }
    const next: Record<string, ChatMessageEvent[]> = { ...state.messagesByRoom };
    for (const [roomId, arr] of Object.entries(byRoom)) {
      next[roomId] = mergeSorted(state.messagesByRoom[roomId] ?? [], arr);
    }
    return { messagesByRoom: next };
  }),
  setMessages: (roomId, msgs) => set((state) => {
    const sorted = mergeSorted([], msgs);
    return { messagesByRoom: { ...state.messagesByRoom, [roomId]: sorted } };
  }),
  setHistoryCursor: (roomId, cursor) => set((state) => ({
    historyCursorByRoom: { ...state.historyCursorByRoom, [roomId]: cursor },
  })),
  setLoadingOlder: (roomId, loading) => set((state) => ({
    loadingOlderByRoom: { ...state.loadingOlderByRoom, [roomId]: loading },
  })),
  setPendingComposeText: (text) => set({ pendingComposeText: text }),
  setRoomReadStates: (roomId, states, myLastReadId) => set((state) => {
    const map: Record<string, string | null> = {};
    for (const s of states) map[s.userId] = s.lastReadId;
    const patch: Partial<ChatState> = {
      readStatesByRoom: { ...state.readStatesByRoom, [roomId]: map },
    };
    // anchor 는 방을 처음 열 때만 기록. 이미 있으면 유지 — 재조회(재연결·멤버 변경) 때마다
    // 최신값으로 덮으면 화면에 떠 있던 "여기까지 읽었습니다" 구분선이 사라진다.
    if (!(roomId in state.unreadAnchorByRoom)) {
      patch.unreadAnchorByRoom = { ...state.unreadAnchorByRoom, [roomId]: myLastReadId };
    }
    return patch;
  }),
  applyReadReceipt: (roomId, userId, lastReadId) => set((state) => {
    const cur = state.readStatesByRoom[roomId];
    if (!cur) return state;   // 아직 초기 조회 전 — 조회가 최신값을 가져온다.
    const prev = cur[userId];
    // 단조 전진만 반영 — 이벤트 재정렬/중복 도달로 배지가 되살아나지 않게.
    if (prev != null && !isReadIdNewer(lastReadId, prev)) return state;
    return {
      readStatesByRoom: {
        ...state.readStatesByRoom,
        [roomId]: { ...cur, [userId]: lastReadId },
      },
    };
  }),
  clearUnreadAnchor: (roomId) => set((state) => {
    if (!(roomId in state.unreadAnchorByRoom)) return state;
    const next = { ...state.unreadAnchorByRoom };
    delete next[roomId];
    return { unreadAnchorByRoom: next };
  }),
  setInputValue: (roomId, value) => set((state) => ({
    inputValueByRoom: { ...state.inputValueByRoom, [roomId]: value },
  })),
  clearInput: (roomId) => set((state) => {
    if (!(roomId in state.inputValueByRoom)) return state;
    const next = { ...state.inputValueByRoom };
    delete next[roomId];
    return { inputValueByRoom: next };
  }),
  toggleAiMode: (roomId) => set((state) => ({
    aiModeByRoom: { ...state.aiModeByRoom, [roomId]: !state.aiModeByRoom[roomId] },
  })),
  setRoomMemberCount: (roomId, count) => set((state) => ({
    roomMemberCountByRoom: { ...state.roomMemberCountByRoom, [roomId]: count },
  })),
  setAiPending: (roomId, pending) => set((state) => ({
    aiPendingByRoom: { ...state.aiPendingByRoom, [roomId]: pending },
    // pending off 시 tool 상태도 함께 clear (안전망 — 응답 미도착 timeout 경로).
    ...(pending ? {} : { aiToolByRoom: { ...state.aiToolByRoom, [roomId]: null } }),
  })),
  setAiTool: (roomId, tool) => set((state) => ({
    aiToolByRoom: { ...state.aiToolByRoom, [roomId]: tool },
  })),
  setSearch: (q) => set({ chatSearch: q }),
  setFilter: (f) => set({ chatFilter: f }),
  setHoveredMsg: (id) => set({ hoveredMsgId: id }),
  setRoomUnreads: (map) =>
    set({ roomUnread: map, chatUnreadTotal: Object.values(map).reduce((a, b) => a + b, 0) }),
  bumpRoomUnread: (roomId) =>
    set((s) => {
      const next = { ...s.roomUnread, [roomId]: (s.roomUnread[roomId] ?? 0) + 1 };
      return { roomUnread: next, chatUnreadTotal: Object.values(next).reduce((a, b) => a + b, 0) };
    }),
  clearRoomUnread: (roomId) =>
    set((s) => {
      if (!s.roomUnread[roomId]) return s;
      const next = { ...s.roomUnread, [roomId]: 0 };
      return { roomUnread: next, chatUnreadTotal: Object.values(next).reduce((a, b) => a + b, 0) };
    }),
  setReplyingTo: (roomId, message) =>
    set((s) => ({ replyingToByRoom: { ...s.replyingToByRoom, [roomId]: message } })),
  clearReplyingTo: (roomId) =>
    set((s) => {
      if (!(roomId in s.replyingToByRoom)) return s;
      const next = { ...s.replyingToByRoom };
      delete next[roomId];
      return { replyingToByRoom: next };
    }),
  applyMyReaction: (messageId, base, emoji, action) =>
    set((s) => {
      const cur = s.reactionsByMessageId[messageId] ?? base;
      const idx = cur.findIndex((r) => r.emoji === emoji);
      let next: ReactionAgg[];
      if (action === 'added') {
        next = idx >= 0
          ? cur.map((r, i) => (i === idx ? { ...r, count: r.count + 1, mine: true } : r))
          : [...cur, { emoji, count: 1, mine: true }];
      } else if (idx >= 0) {
        const dec = { ...cur[idx]!, count: cur[idx]!.count - 1, mine: false };
        next = dec.count <= 0 ? cur.filter((_, i) => i !== idx) : cur.map((r, i) => (i === idx ? dec : r));
      } else {
        next = cur;
      }
      return { reactionsByMessageId: { ...s.reactionsByMessageId, [messageId]: next } };
    }),
  toggleDept: (key) => set((state) => ({
    expandedDept: { ...state.expandedDept, [key]: !state.expandedDept[key] },
  })),
  updateAction: (actionId, status) => set((state) => {
    if (state.actionsByActionId[actionId] === status) return state;
    return { actionsByActionId: { ...state.actionsByActionId, [actionId]: status } };
  }),
  appendChunk: (roomId, streamMessageId, delta) => set((state) => {
    if (!delta) return state; // 빈 chunk no-op (참조 변경 차단)
    const room = state.streamingByRoom[roomId] ?? {};
    const cur = room[streamMessageId];
    const now = new Date().toISOString();
    const nextMsg: StreamingMessage = cur
      ? { ...cur, text: cur.text + delta, lastChunkAt: now }
      : {
          streamMessageId,
          text: delta,
          isStreaming: true,
          createdAt: now,
          lastChunkAt: now,
        };
    return {
      streamingByRoom: {
        ...state.streamingByRoom,
        [roomId]: { ...room, [streamMessageId]: nextMsg },
      },
    };
  }),
  finalizeStream: (roomId, streamMessageId, finalText) => set((state) => {
    const room = state.streamingByRoom[roomId] ?? {};
    const cur = room[streamMessageId];
    // delta 누락으로 임시 메시지가 부재해도 final 텍스트로 단독 entry 생성 (graceful).
    // F-LOW-1 fix (followup-D): finalize 직후 isStreaming=false → evict 대상 아님.
    // lastChunkAt 갱신은 dead path 라 cur 의 마지막 chunk 시각 그대로 유지 (또는 신규 entry 만 now).
    const nextMsg: StreamingMessage = cur
      ? { ...cur, text: finalText, isStreaming: false }
      : {
          streamMessageId,
          text: finalText,
          isStreaming: false,
          createdAt: new Date().toISOString(),
          lastChunkAt: new Date().toISOString(),
        };
    return {
      streamingByRoom: {
        ...state.streamingByRoom,
        [roomId]: { ...room, [streamMessageId]: nextMsg },
      },
    };
  }),
  removeStream: (roomId, streamMessageId) => set((state) => {
    const room = state.streamingByRoom[roomId];
    if (!room || !(streamMessageId in room)) return state;
    const { [streamMessageId]: _drop, ...rest } = room;
    return {
      streamingByRoom: { ...state.streamingByRoom, [roomId]: rest },
    };
  }),
  /**
   * R4 fix (followup-cycle-streaming-C-leak) — anthropic timeout 등으로 ai.complete 미발화 시
   * isStreaming=true partial entry 가 영속 잔존하는 leak 안전망.
   *
   * 정책: lastChunkAt 이 maxAgeMs 이상 경과한 isStreaming=true entry 만 제거.
   * isStreaming=false (finalize 됨) entry 는 cleanup useEffect 의 정확/fallback 매칭이 정리하므로 건드리지 않음.
   *
   * F-MED-2 fix (followup-D): 함수 분리 (evictStaleStreamsInRoom + evictStaleStreamsAll).
   * 공통 로직은 _evictStaleStreams(state, targetRooms, maxAgeMs) 내부 helper.
   */
  evictStaleStreamsInRoom: (roomId, maxAgeMs) => set((state) =>
    _evictStaleStreams(state, [roomId], maxAgeMs),
  ),
  evictStaleStreamsAll: (maxAgeMs) => set((state) =>
    _evictStaleStreams(state, Object.keys(state.streamingByRoom), maxAgeMs),
  ),
  reset: () => set({ ...INITIAL_STATE }),
}));

export const __test__ = { insertSorted, mergeSorted, safeParseSeq };
